/**
 * Ticket Lifecycle v2 — Slice 5D-1
 *
 * Explicit "Reassign Advisor" dialog. Opens from `TicketKebabMenu` when:
 *   - `custodyHandoff` feature flag is ON
 *   - the ticket's `ticket_custody.custody_owner === 'FOH'`
 *   - the actor has a role permitted by the edge-function role gate
 *
 * Calls `useTicketCustodyMutation` with `eventKind: 'reassign_within_foh'`
 * and the picked `to_user_id`. The edge function + `_apply_custody_transition`
 * RPC validate the legal transition; this dialog does not duplicate that
 * logic.
 *
 * Boundary: this component touches custody mutation + member listing
 * only. It does NOT touch Tech OptiTime, Lifecycle OptiTime prediction,
 * Chronomancer, scheduling, smart assign, timers, parking, rollover, or
 * bay assignment (per slice 5D-1 directive).
 *
 * Display-only with respect to advisor metrics: the per-bucket / historical
 * advisor average path is explicitly out of scope for 5D-1.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useTicketCustodyMutation } from '@/hooks/tickets/useTicketCustody';
import {
  useFohCapableMembers,
  type FohCapableMember,
} from '@/hooks/tickets/useFohCapableMembers';

export interface ReassignAdvisorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  shopId: string;
  /** UUID of the current `ticket_custody.holder_user_id`, when known. */
  currentHolderUserId: string | null;
  /**
   * Optional. Display label for the current holder; renders above the
   * picker so the actor can see who they are reassigning away from.
   * When the holder's user_id is in the member list, this can default
   * to that row's `displayName`; the prop allows the caller to override
   * when the holder is NOT in the FOH-capable cohort (e.g. legacy data).
   */
  currentHolderLabel?: string | null;
}

const DEFAULT_REASON = '';

/**
 * Pure helper exported for tests: choose the label shown for the
 * "current holder" line. Prefers an explicit label prop, then the
 * matched member's display name, then a UUID-truncated fallback.
 */
export function formatCurrentHolderLabel(
  currentHolderUserId: string | null,
  currentHolderLabel: string | null | undefined,
  members: ReadonlyArray<FohCapableMember>,
): string {
  if (currentHolderLabel && currentHolderLabel.trim()) return currentHolderLabel.trim();
  if (!currentHolderUserId) return 'No advisor currently holds this ticket.';
  const matched = members.find((m) => m.userId === currentHolderUserId);
  if (matched) return matched.displayName;
  return `User ${currentHolderUserId.slice(0, 8)}…`;
}

export function ReassignAdvisorDialog({
  open,
  onOpenChange,
  ticketId,
  shopId,
  currentHolderUserId,
  currentHolderLabel,
}: ReassignAdvisorDialogProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [reason, setReason] = useState<string>(DEFAULT_REASON);

  const membersQuery = useFohCapableMembers({ shopId });
  const mutation = useTicketCustodyMutation();

  // Reset local form state whenever the dialog closes (or the target
  // ticket changes). Avoids carrying a stale selection across opens.
  useEffect(() => {
    if (!open) {
      setSelectedUserId('');
      setReason(DEFAULT_REASON);
    }
  }, [open, ticketId]);

  const members = membersQuery.data ?? [];
  const holderLabel = useMemo(
    () => formatCurrentHolderLabel(currentHolderUserId, currentHolderLabel, members),
    [currentHolderUserId, currentHolderLabel, members],
  );

  const selectableMembers = members.filter((m) => m.userId !== currentHolderUserId);
  const noMembers = !membersQuery.isLoading && selectableMembers.length === 0;

  const canConfirm =
    !!selectedUserId &&
    selectedUserId !== currentHolderUserId &&
    !mutation.isPending;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      await mutation.mutateAsync({
        ticketId,
        eventKind: 'reassign_within_foh',
        toUserId: selectedUserId,
        reason: reason.trim() ? reason.trim() : 'manual_reassign',
      });
      onOpenChange(false);
    } catch {
      // mutation `onError` surfaces a toast; keep dialog open so the
      // actor can adjust selection / retry without re-opening.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reassign-advisor-dialog">
        <DialogHeader>
          <DialogTitle>Reassign Advisor</DialogTitle>
          <DialogDescription>
            Hand this ticket's FOH custody to a different advisor. The
            change is recorded in the custody timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              Currently held by
            </Label>
            <p
              className="text-sm font-medium"
              data-testid="reassign-advisor-current-holder"
            >
              {holderLabel}
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="reassign-advisor-target">New advisor</Label>
            {membersQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading FOH-capable members…
              </div>
            ) : membersQuery.isError ? (
              <p className="text-sm text-destructive">
                Failed to load shop members.
              </p>
            ) : noMembers ? (
              <p className="text-sm text-muted-foreground">
                No other FOH-capable members are available in this shop.
              </p>
            ) : (
              <Select
                value={selectedUserId}
                onValueChange={setSelectedUserId}
              >
                <SelectTrigger
                  id="reassign-advisor-target"
                  data-testid="reassign-advisor-target"
                >
                  <SelectValue placeholder="Select an advisor…" />
                </SelectTrigger>
                <SelectContent>
                  {selectableMembers.map((m) => (
                    <SelectItem
                      key={m.userId}
                      value={m.userId}
                      data-testid={`reassign-advisor-option-${m.userId}`}
                    >
                      {m.displayName}{' '}
                      <span className="text-xs text-muted-foreground">
                        ({m.role})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="reassign-advisor-reason">
              Reason{' '}
              <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="reassign-advisor-reason"
              data-testid="reassign-advisor-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. covering for lunch break"
              maxLength={200}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="reassign-advisor-confirm"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Reassigning…
              </>
            ) : (
              'Reassign Advisor'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
